/**
 * WHAT HAPPENS AFTER THE CARD IS AUTHORIZED — the copy the awaiting-acceptance
 * card prints, driven by the `fulfillment` object snowclub-be puts on the
 * customer order view.
 *
 * An operator confirms a booking one of two ways:
 *   - `explicit_acceptance`: they must say yes.
 *   - `no_objection`: a verified request was sent to them, and if they have
 *     not objected by `deadlineAt` the booking can become accepted.
 *
 * The no-objection deadline wording is only printed when the server supplied
 * a deadline we can actually parse. A no-objection order without one gets
 * neutral awaiting-operator copy: it must not claim the operator "has to
 * accept" (they may not), nor that a request was verifiably sent or that the
 * booking will be deemed accepted, and it must not invent a deadline. The
 * explicit copy is kept for `explicit_acceptance` and absent/unknown modes.
 *
 * Nothing here may say the order is paid: the backend authorizes with manual
 * capture, and only the literal `paid` status prints a paid screen.
 */

export interface Fulfillment {
  mode: "explicit_acceptance" | "no_objection";
  status?: string;
  deadlineAt?: string;
}

/** Read `fulfillment` off an order as it came over the wire. Anything that is
 * not one of the two known modes is treated as absent. */
export function parseFulfillment(raw: unknown): Fulfillment | null {
  if (!raw || typeof raw !== "object") return null;
  const f = raw as Record<string, unknown>;
  if (f.mode !== "explicit_acceptance" && f.mode !== "no_objection") {
    return null;
  }
  return {
    mode: f.mode,
    status: typeof f.status === "string" ? f.status : undefined,
    deadlineAt: typeof f.deadlineAt === "string" ? f.deadlineAt : undefined,
  };
}

/** The no-objection deadline, or null when there is none to print (explicit
 * mode, no fulfillment, or a missing/unparseable deadline). */
export function noObjectionDeadline(
  fulfillment: Fulfillment | null | undefined,
): Date | null {
  if (fulfillment?.mode !== "no_objection" || !fulfillment.deadlineAt) {
    return null;
  }
  const deadline = new Date(fulfillment.deadlineAt);
  return Number.isNaN(deadline.getTime()) ? null : deadline;
}

/**
 * Polls a just-authorized order gets for its fulfillment record to land
 * before an `explicit_acceptance` (or absent) summary is believed. ~10s at the
 * checkout's 2s interval, counted from the first `pending_acceptance` read.
 */
export const FULFILLMENT_GRACE_POLLS = 5;

/**
 * True while a screen should keep polling instead of settling on
 * `pending_acceptance`, because the copy it would print may not be final yet.
 *
 * snowclub-be's webhook writes `pending_acceptance` FIRST and only then
 * creates the no-objection request (webhook.ts → startNoObjection). In that
 * gap the order view reports `explicit_acceptance` — its default for an order
 * with no request — so a no-objection order settled at first sight told the
 * customer the operator "has to accept". A no-objection request without a
 * deadline is still waiting on its delivery receipt, so it is held too.
 *
 * Only held when THIS visit just authorized the card: a reload reads a record
 * that has had its chance to land. After FULFILLMENT_GRACE_POLLS sightings the
 * summary is believed, so a genuine explicit-acceptance order still gets its
 * explicit copy.
 */
export function fulfillmentStillLanding({
  status,
  fulfillment,
  justAuthorized,
  sightings,
}: {
  status: string | null | undefined;
  fulfillment: unknown;
  justAuthorized: boolean;
  /** consecutive polls, including this one, that read `pending_acceptance` */
  sightings: number;
}): boolean {
  if (status !== "pending_acceptance" || !justAuthorized) return false;
  if (sightings >= FULFILLMENT_GRACE_POLLS) return false;
  const f = parseFulfillment(fulfillment);
  if (f?.mode !== "no_objection") return true;
  if (noObjectionDeadline(f)) return false;
  return !f.status || f.status === "awaiting_operator";
}

// dateStyle/timeStyle cannot be combined with timeZoneName — the constructor
// throws "TypeError: Invalid option" — so spell out the components instead.
export const formatDeadline = (d: Date) =>
  new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(d);

export function acceptanceCopy({
  fulfillment,
  cardAuthorized,
  format = formatDeadline,
}: {
  fulfillment: Fulfillment | null | undefined;
  cardAuthorized: boolean;
  format?: (d: Date) => string;
}): { heading: string; body: string } {
  const notCharged = cardAuthorized
    ? "Your card has been authorized but not charged."
    : "You have not been charged.";
  const deadline = noObjectionDeadline(fulfillment);

  if (deadline) {
    return {
      heading: cardAuthorized
        ? "Payment authorized — awaiting operator confirmation"
        : "Awaiting operator confirmation",
      body:
        `${notCharged} We have sent the operator a verified request for ` +
        `this booking. If they do not object by ${format(deadline)}, the ` +
        "booking can become accepted. You are charged only once the booking " +
        "is fully confirmed; if the operator objects before that deadline, you are not charged.",
    };
  }

  if (fulfillment?.mode === "no_objection") {
    return {
      heading: cardAuthorized
        ? "Payment authorized — awaiting the operator"
        : "Awaiting the operator",
      body:
        `${notCharged} We are checking the operator request and will update ` +
        "this order when its response window is confirmed. You are charged " +
        "only once the booking is fully confirmed; if it is not confirmed, " +
        "you are not charged.",
    };
  }

  return {
    heading: cardAuthorized
      ? "Payment authorized — awaiting operator acceptance"
      : "Awaiting operator acceptance",
    body:
      `${notCharged} The operator now has to accept this booking. You are ` +
      "charged only if they accept; if they decline, you are not charged.",
  };
}
